import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';

const WHATSAPP_NUMBER = '918880331224';

export default function ConsultationPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    projectType: '',
    message: ''
  });

  useEffect(() => {
    // Show popup after 15 seconds
    const timer = setTimeout(() => {
      const hasSeenPopup = sessionStorage.getItem('consultationPopupSeen');
      if (!hasSeenPopup) {
        setIsOpen(true);
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('consultationPopupSeen', 'true');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = encodeURIComponent(
      `*New Consultation Request*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*Project Type:* ${formData.projectType}\n` +
      `*Message:* ${formData.message}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    handleClose();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="consultation-popup"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-white border border-gray-200 p-8"
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors"
              data-testid="popup-close-btn"
            >
              <X size={24} />
            </button>

            <div className="text-center mb-8">
              <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
                Limited Time Offer
              </span>
              <h3 className="font-heading text-3xl text-gray-900 mt-2">
                Free Consultation
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                Get expert advice on your dream project today
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="popup-name-input"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
                className="form-input"
                data-testid="popup-phone-input"
              />
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                required
                className="form-input bg-transparent"
                data-testid="popup-project-select"
              >
                <option value="" disabled className="bg-white">Select Project Type</option>
                <option value="Residential Construction" className="bg-white">Residential Construction</option>
                <option value="Commercial Construction" className="bg-white">Commercial Construction</option>
                <option value="Interior Design" className="bg-white">Interior Design</option>
                <option value="Renovation" className="bg-white">Renovation</option>
                <option value="Turnkey Project" className="bg-white">Turnkey Project</option>
              </select>
              <textarea
                name="message"
                placeholder="Brief about your project..."
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="form-input resize-none"
                data-testid="popup-message-input"
              />
              <button
                type="submit"
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="popup-submit-btn"
              >
                <Send size={18} />
                Book Free Consultation
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
